import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { overlay } from 'overlay-kit';

import Button from '@/components/Button/Button';
import Menu from '@/components/Menu/Menu';
import type { AggregatedReportFolder } from '@/entities/report';
import {
  deleteAggregatedReportFolderMutationOptions,
  updateAggregatedReportFolderMutationOptions,
} from '@/entities/report';
import { errorHandler } from '@/error-handler';
import { usePopup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import { clipboardCopy } from '@/utils/util';

import { ReportFolderFormDialog } from './report-folder-form-dialog';

type Props = {
  workspaceId: string;
  folder: AggregatedReportFolder;
};

export function ReportFolderActionMenu({ workspaceId, folder }: Props) {
  const queryClient = useQueryClient();
  const setPopup = usePopup();
  const updateFolderMutation = useMutation(
    updateAggregatedReportFolderMutationOptions(queryClient),
  );
  const deleteFolderMutation = useMutation(
    deleteAggregatedReportFolderMutationOptions(queryClient),
  );

  const openRenameOverlay = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <ReportFolderFormDialog
        mode="rename"
        defaultName={folder.name}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
        onSubmit={async (name) => {
          try {
            await updateFolderMutation.mutateAsync({
              workspace_id: workspaceId,
              folder_id: folder.alias,
              name,
            });
            toast('Folder renamed', 'success');
          } catch (error) {
            toast(errorHandler(error, 'Failed to rename folder'), 'fail');
            throw error;
          }
        }}
      />
    ));
  };

  const copyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('folder_id', folder.alias);
    clipboardCopy(url.toString());
    toast('Folder link copied', 'success');
  };

  const confirmDelete = () => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete folder',
      message:
        'The folder will be deleted. Reports inside it will move to All reports.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        void (async () => {
          try {
            await deleteFolderMutation.mutateAsync({
              workspace_id: workspaceId,
              folder_id: folder.alias,
            });
            toast('Folder deleted', 'success');
          } catch (error) {
            toast(errorHandler(error, 'Failed to delete folder'), 'fail');
          }
        })();
      },
    });
  };

  return (
    <div
      className="flex w-full justify-end"
      onClick={(event) => event.stopPropagation()}
    >
      <Menu
        triggerNode={
          <Button
            variant="ghost_secondary"
            sizeType="s"
            aria-label={`Open actions for ${folder.name}`}
            leftIcon={<MoreHorizontal />}
          />
        }
        options={[
          { label: 'Rename', onClick: openRenameOverlay },
          { label: 'Copy link', onClick: copyLink },
          { label: 'Delete', onClick: confirmDelete },
        ]}
      />
    </div>
  );
}
